import { faCircleQuestion as fasCircleQuestion } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ResponsiveHelper } from "@utils/responsive-helper";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import type React from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import type { FieldRenderProps } from "react-final-form";
import type { FormItemProps } from "./FormItem";
import { FormItem } from "./FormItem";

export interface FormCheckProps extends FormItemProps<boolean> {
	type?: "checkbox" | "switch";
}

export class FormCheck extends FormItem<boolean, FormCheckProps> {
	protected renderInline(fieldProps: FieldRenderProps<boolean>): ReactNode {
		const { name, info } = this.props;

		return (
			<Form.Group controlId={name} className={this.getGroupClassName(fieldProps)}>
				{this.renderCheckbox(fieldProps)}
				{this.renderInfo(info)}
				{this.renderValidation(fieldProps)}
			</Form.Group>
		);
	}

	protected renderCol(fieldProps: FieldRenderProps<boolean>): ReactNode {
		return this.renderInline(fieldProps);
	}

	private renderCheckbox(fieldProps: FieldRenderProps<boolean>): ReactNode {
		const { name } = this.props;
		const {
			input: { value },
		} = fieldProps;
		let { infoTooltip } = this.props;
		let tooltipVariant: "black" | "danger" = "black";

		if (this.isInvalid(fieldProps)) {
			infoTooltip = this.handleValidate(value, []);
			tooltipVariant = "danger";
		}

		if (infoTooltip) {
			if (ResponsiveHelper.mediaBreakPointDown("md")) {
				return (
					<div className="d-inline">
						{this.renderField(fieldProps)}
						<OverlayTrigger
							overlay={(props: OverlayInjectedProps) => (
								<Tooltip
									id={`infoTooltip-mobil-${name}`}
									className={`tooltip-${tooltipVariant}`}
									{...props}
								>
									{infoTooltip}
								</Tooltip>
							)}
						>
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1 icon_touch_fix" />
						</OverlayTrigger>
					</div>
				);
			}

			return (
				<OverlayTrigger
					overlay={(props: OverlayInjectedProps) => (
						<Tooltip id={`infoTooltip-${name}`} className={`tooltip-${tooltipVariant}`} {...props}>
							{infoTooltip}
						</Tooltip>
					)}
				>
					<div className="d-inline">{this.renderField(fieldProps)}</div>
				</OverlayTrigger>
			);
		}

		return this.renderField(fieldProps);
	}

	protected renderField(fieldProps: FieldRenderProps<boolean>): ReactNode {
		const { name, label, readOnly, tabIndex, disabled, type = "checkbox", infoTooltip } = this.props;
		const {
			input: { value },
			meta: { valid, touched },
		} = fieldProps;
		const isInvalid = this.isInvalid(fieldProps);
		let inputGroupClass = "";

		if (!disabled && touched && valid) {
			//	inputGroupClass = 'has-success';
		} else if (isInvalid) {
			inputGroupClass = "has-error";
		}

		return (
			<Form.Check
				custom
				className={inputGroupClass}
				isInvalid={isInvalid}
				readOnly={readOnly}
				disabled={disabled || readOnly}
				tabIndex={tabIndex}
				type={type}
				name={name}
				id={this.id}
				label={
					infoTooltip ? (
						<>
							{label}

							{ResponsiveHelper.mediaBreakPointUp("md") && (
								<span className="text-nowrap">
									&nbsp;
									<FontAwesomeIcon
										icon={fasCircleQuestion}
										className={ResponsiveHelper.mediaBreakPointDown("md") ? "icon_touch_fix" : ""}
									/>
								</span>
							)}
						</>
					) : (
						label
					)
				}
				checked={typeof value === "string" ? (value as unknown as string) === "true" : !!value}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					this.handleChange(event.currentTarget.checked, fieldProps);
				}}
				onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleFocus(event, fieldProps);
				}}
				onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleBlur(event, fieldProps);
				}}
			/>
		);
	}

	@autobind
	protected handleValidate(value: boolean, allValues: Record<string, any>): string {
		const { required, validate } = this.props;

		if (this.isDisabled()) {
			return undefined;
		}

		if (!value && required && validate === undefined) {
			return "Kötelező mező!";
		}

		return super.handleValidate(value, allValues);
	}

	protected getGroupClassName(fieldProps: FieldRenderProps<boolean>): string {
		const { disabled, required, className, readOnly, label } = this.props;
		const {
			meta: { invalid, valid, touched },
		} = fieldProps;

		return compact([
			className,
			!label && "without-label",
			required && "required",
			disabled && "disabled",
			!disabled && !readOnly && touched && invalid && required && "has-danger",
			!disabled && !readOnly && touched && valid && "has-success",
		]).join(" ");
	}

	private isInvalid(fieldProps: FieldRenderProps<boolean>): boolean {
		const { disabled, required = false } = this.props;
		const {
			meta: { invalid, touched },
		} = fieldProps;

		return !disabled && touched && invalid && required;
	}
}
